import React, { useState, useEffect } from "react";
import { useUser } from "../context/UserContext";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  Snackbar,
  Button,
  TextField,
  Dialog,
  Alert as MuiAlert,
} from "@mui/material";
import {
  MoreVert,
  Star,
  StarBorder,
  Visibility as VisibilityIcon,
  ShareOutlined,
  Close as CloseIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import "./Dashboard.css";
import gradientBg from "../assets/gradient-bg.jpg";

const Dashboard = () => {
  const user = useUser();

  const [rows, setRows] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [searchText, setSearchText] = useState("");
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryInput, setCategoryInput] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);

  // Fullscreen dashboard state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentDashboardUrl, setCurrentDashboardUrl] = useState("");
  const [currentDashboardTitle, setCurrentDashboardTitle] = useState("");

  // Fetch dashboards in real-time
  useEffect(() => {
    if (!user?.user?.organizationId) return;
    const q = query(
      collection(db, "dashboards"),
      where("organizationId", "==", user.user.organizationId)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dashboards = snapshot.docs.map((doc, index) => ({
        id: doc.id,
        sno: index + 1,
        ...doc.data(),
      }));
      setRows(dashboards);
    });
    return () => unsubscribe();
  }, [user]);

  // Fetch projects for category dropdown if needed
  useEffect(() => {
    const fetchProjects = async () => {
      const snapshot = await getDocs(collection(db, "projects"));
      const options = snapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
      }));
      setProjectOptions(options);
    };
    fetchProjects();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      showSnackbar("Link copied to clipboard!", "success");
    });
  };

  const handleFavoriteToggle = async (id) => {
    const updatedRow = rows.find((r) => r.id === id);
    if (!updatedRow) return;
    const newValue = !updatedRow.isFavorite;
    await updateDoc(doc(db, "dashboards", id), { isFavorite: newValue });
  };

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const handleAddEditCategory = async () => {
    if (!categoryInput.trim()) return;
    await updateDoc(doc(db, "dashboards", menuRowId), {
      category: categoryInput.trim(),
    });
    setDialogOpen(false);
    setCategoryInput("");
    handleMenuClose();
    showSnackbar("Category updated!", "success");
  };

  const handleDeleteCategory = async () => {
    await updateDoc(doc(db, "dashboards", menuRowId), { category: "" });
    handleMenuClose();
    showSnackbar("Category deleted!", "info");
  };

  // Fullscreen handlers
  const openFullscreen = (url, title) => {
    setCurrentDashboardUrl(url);
    setCurrentDashboardTitle(title);
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    setCurrentDashboardUrl("");
    setCurrentDashboardTitle("");
  };

  const filteredRows = rows
    .filter((row) => row.title.toLowerCase().includes(searchText.toLowerCase()))
    .filter((row) =>
      filter === "all"
        ? true
        : filter === "favorites"
        ? row.isFavorite
        : !row.isFavorite
    )
    .map((row, index) => ({ ...row, sno: index + 1 }));

  const columns = [
    { field: "title", headerName: "Dashboard Name", flex: 1 },
    { field: "category", headerName: "Category", flex: 1 },
    {
      field: "favorite",
      headerName: "Favorite",
      width: 120,
      renderCell: (params) => (
        <Tooltip
          title={params.row.isFavorite ? "Unmark Favorite" : "Mark Favorite"}
        >
          <IconButton
            onClick={() => handleFavoriteToggle(params.row.id)}
            sx={{ "&:hover": { backgroundColor: "transparent" } }}
          >
            {params.row.isFavorite ? (
              <Star sx={{ color: "#ad1010" }} />
            ) : (
              <StarBorder sx={{ color: "#ad1010" }} />
            )}
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 360,
      sortable: false,
      renderCell: (params) => {
        const { url, title } = params.row;
        return (
          <Box display="flex" gap={1} mt={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={<VisibilityIcon />}
              sx={{
                textTransform: "none",
                paddingBottom: "1px",
                backgroundColor: "transparent",
                color: "#645792",
                border: "1px solid #645792",
                "&:hover": {
                  backgroundColor: "#645792",
                  color: "#fff",
                },
              }}
              onClick={() => {
                setSelectedDashboard(params.row);
                setViewOpen(true);
              }}
            >
              View
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<ShareOutlined />}
              sx={{
                textTransform: "none",
                paddingBottom: "1px",
                backgroundColor: "transparent",
                color: "#645792",
                border: "1px solid #645792",
                "&:hover": {
                  backgroundColor: "#645792",
                  color: "#fff",
                },
              }}
              onClick={() => handleCopyLink(url)}
            >
              Share
            </Button>
          </Box>
        );
      },
    },
    {
      field: "more",
      headerName: "",
      width: 60,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuOpen(e, params.row.id)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <Box p={4} sx={{ width: "100%", overflowX: "auto", position: "relative" }}>
      <Box sx={{ width: "100%", maxWidth: "1200px", mx: "auto" }}>
        {/* Search + Filter */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          mb={2}
        >
          <TextField
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search dashboard"
            size="small"
            sx={{ width: 200, backgroundColor: "rgba(255,255,255,0.3)" }}
          />
          <Tooltip
            title={filter === "favorites" ? "Show All" : "Show Favorites"}
          >
            <IconButton
              onClick={() =>
                setFilter(filter === "favorites" ? "all" : "favorites")
              }
              sx={{ "&:hover": { backgroundColor: "transparent" } }}
            >
              {filter === "favorites" ? (
                <Star sx={{ color: "#ad1010" }} />
              ) : (
                <StarBorder sx={{ color: "#ad1010" }} />
              )}
              <Typography sx={{ fontSize: "1rem", ml: 0.5 }}>
                Favorite
              </Typography>
            </IconButton>
          </Tooltip>
        </Box>

        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontFamily: "Lato", color: "#645792", fontWeight: "bold" }}
        >
          Dashboards
        </Typography>

        {/* DataGrid */}
        {!viewOpen && (
          <Box
            sx={{
              width: "100%",
              transition: "all 0.3s ease",
              overflow: "hidden",
              flexGrow: 1,
            }}
          >
            <DataGrid
              className="grid"
              rows={filteredRows}
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              sx={{
                mt: 5,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.3)",
                "& .MuiDataGrid-columnHeaders": {
                  color: "black",
                  fontWeight: "bold",
                  fontSize: "1rem",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "rgba(180, 87, 146,0.1)",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "rgba(100, 87, 146, 0.1)",
                },
              }}
            />
          </Box>
        )}

        {/* View Mode */}
        {viewOpen && selectedDashboard && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              border: "1px solid #ddd",
              borderRadius: 2,
              boxShadow: 3,
              backgroundColor: "rgba(180, 87, 146,0.1)",
            }}
          >
            <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
              <Button
                variant="contained"
                sx={{
                  textTransform: "none",
                  paddingBottom: "1px",
                  backgroundColor: "transparent",
                  color: "#645792",
                  border: "1px solid #645792",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#645792",
                    color: "#fff",
                  },
                }}
                onClick={() =>
                  openFullscreen(selectedDashboard.url, selectedDashboard.title)
                }
              >
                Full Screen
              </Button>
              <Button
                variant="contained"
                color="secondary"
                sx={{
                  textTransform: "none",
                  paddingBottom: "1px",
                  backgroundColor: "transparent",
                  color: "#645792",
                  border: "1px solid #645792",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#645792",
                    color: "#fff",
                  },
                }}
                onClick={() => {
                  setViewOpen(false);
                  setSelectedDashboard(null);
                }}
              >
                Close
              </Button>
            </Box>
            <Box
              sx={{
                width: "100%",
                height: "70vh",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: 2,
              }}
            >
              <iframe
                src={selectedDashboard.url}
                width="100%"
                height="100%"
                style={{ border: "none" }}
                title={selectedDashboard.title}
              />
            </Box>
          </Box>
        )}

        {/* Category Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              const currentCategory =
                rows.find((r) => r.id === menuRowId)?.category || "";
              setCategoryInput(currentCategory);
              setDialogOpen(true);
            }}
          >
            {rows.find((r) => r.id === menuRowId)?.category
              ? "Edit Category"
              : "Add Category"}
          </MenuItem>
          <MenuItem onClick={handleDeleteCategory}>
            <Typography sx={{ fontSize: "1rem", ml: 0.5 }}>
              Delete Category
            </Typography>
          </MenuItem>
        </Menu>

        {/* Category Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "1.2rem", pb: 1 }}>
              Set Category
            </Typography>
            <TextField
              fullWidth
              label="Category"
              value={categoryInput}
              onChange={(e) => setCategoryInput(e.target.value)}
              sx={{ backgroundColor: "rgba(255,255,255,0.9)", width: "400px" }}
              autoFocus
            />
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Button
                onClick={() => setDialogOpen(false)}
                sx={{ color: "#645792", fontWeight: "bold" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddEditCategory}
                variant="contained"
                disabled={!categoryInput.trim()}
                sx={{
                  backgroundColor: "#645792",
                  fontWeight: "bold",
                  "&:hover": { backgroundColor: "#543a75" },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Dialog>

        {/* Fullscreen Overlay */}
        {isFullscreen && currentDashboardUrl && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundImage: `url(${gradientBg})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center center",
              backgroundSize: "cover",
              zIndex: 1300,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Top bar with close */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                p: 1,
                backgroundColor: "#645792",
              }}
            >
              <Tooltip title="Close">
                <IconButton onClick={closeFullscreen} sx={{ color: "white" }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
            {/* Iframe */}
            <Box sx={{ flexGrow: 1 }}>
              <iframe
                src={currentDashboardUrl}
                title={currentDashboardTitle}
                width="100%"
                height="100%"
                style={{ border: "none" }}
                allowFullScreen
              />
            </Box>
          </Box>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Dashboard;
